<template>
  <div>
    <a-form
      ref="formRef"
      layout="vertical"
      :model="form"
    >
      <a-card :bordered="false">
        <a-form-item
          ref="content"
          name="content"
          :auto-link="false"
          :rules="{
            type: 'string',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <MentionTextarea
            v-model:value="form.content"
            :form-item-ref="$refs.content"
            :mentioned-user-ids="form.mentionUserIds"
            search-range="ALL"
            :placeholder="$t('okr.editObjectiveDialog.contentPlaceholder')"
          />
          <!-- <template #label>
            <a-space>
              <IconFont
                type="iconmubiao"
                :style="{ fontSize: '20px' }"
              />
              {{ $t('shared.objective') }}
            </a-space>
          </template> -->
        </a-form-item>
        <!-- 是否为财务目标 -->
        <a-form-item
          :label="$t('okr.editObjectiveDialog.whetherIsAboutFinance')"
          name="isFinancial"
          :rules="{
            type: 'boolean',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <a-radio-group v-model:value="form.isFinancial">
            <a-radio :value="true">
              {{ $t('okr.radio.yes') }}
            </a-radio>
            <a-radio :value="false">
              {{ $t('okr.radio.no') }}
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <!-- 权重 -->
        <a-form-item
          :label="$t('shared.proportion')"
          name="weight"
          :rules="{
            type: 'number',
            required: true,
            message: $t('okr.formRules.required.shared'),
            trigger: 'blur'
          }"
        >
          <a-input-number
            v-model:value="form.weight"
            name="weight"
            :min="0"
            :max="100"
            class="input"
            :step="1"
            :precision="0"
            :formatter="value => `${value}`"
          />
          <span class="after">/100</span>
        </a-form-item>
        <div class="title">
          {{ $t('okr.list.performanceIndicators') }}
        </div>
        <!-- SAB 考核标准相关字段 -->
        <a-form-item
          :label="$t('okr.editObjectiveDialog.hideOrNot')"
          name="performanceIsHide"
          :rules="{
            type: 'boolean',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <a-radio-group v-model:value="form.performanceIsHide">
            <a-radio :value="true">
              {{ $t('okr.radio.hide') }}
              <a-popover placement="top">
                <template #content>
                  <span>{{ $t('okr.editObjectiveDialog.hideOrNotTip') }}</span>
                </template>
                <span class="tip">?</span>
              </a-popover>
            </a-radio>
            <a-radio :value="false">
              {{ $t('okr.radio.not') }}
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item
          :label="
            `${$t('okr.list.superior')}（${$t(
              'okr.list.superiorPlaceholder'
            )}）`
          "
          :name="['performanceObjective', 'superior']"
          :rules="{
            type: 'string',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <a-textarea
            v-model:value="form.performanceObjective.superior"
            :placeholder="$t('okr.list.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }"
          />
        </a-form-item>
        <a-form-item
          :label="
            `${$t('okr.list.excellent')}（${$t(
              'okr.list.excellentPlaceholder'
            )}）`
          "
          :name="['performanceObjective', 'excellent']"
          :rules="{
            type: 'string',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <a-textarea
            v-model:value="form.performanceObjective.excellent"
            :placeholder="$t('okr.list.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }"
          />
        </a-form-item>
        <a-form-item
          :label="$t('okr.list.target')"
          :name="['performanceObjective', 'target']"
          :rules="{
            type: 'string',
            required: true,
            message: $t('okr.formRules.required.objective'),
            trigger: 'blur'
          }"
        >
          <a-textarea
            v-model:value="form.performanceObjective.target"
            :placeholder="$t('okr.list.placeholder')"
            :auto-size="{ minRows: 3, maxRows: 6 }"
          />
        </a-form-item>
      </a-card>
    </a-form>
    <FormAction
      :loading="loading"
      :can-close="canClose"
      @cancel="$emit('cancel')"
      @save="save()"
    />
  </div>
</template>

<script>
import { ref, toRefs, unref, watch, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import { replaceStr2Html } from '@/utils/line-break'
import { updateObjective } from '@/api/okr'
import { editObjectiveAuth, removeObjectiveAuth } from '@/api/auth'
import useMention from '@/composables/use-mention'
import MentionTextarea from '@/components/MentionTextarea'
import FormAction from './FormAction'

export default {
  name: 'EditObjectiveForm',
  components: {
    MentionTextarea,
    FormAction
  },
  props: {
    objectiveId: {
      type: Number,
      required: true
    },
    initForm: {
      type: Object,
      required: true
    },
    canClose: {
      type: Boolean,
      required: true
    }
  },
  emits: ['update:can-close', 'objective-saved', 'cancel'],
  setup (props, { emit }) {
    const { t } = useI18n()
    const { getFormattedUserIds, getAddUserIds, getRmUserIds } = useMention()
    const formRef = ref()
    const { initForm } = toRefs(props)
    const form = unref(initForm)
    const loading = ref(false)
    const originalMentionUserIds = [...toRaw(form.mentionUserIds)]
    watch(form, () => {
      emit('update:can-close', false)
    })

    async function save () {
      loading.value = true
      try {
        await formRef.value.validate()
      } catch (_) {
        loading.value = false
        return
      }

      const data = {
        content: replaceStr2Html(form.content),
        weight: form.weight,
        is_financial: form.isFinancial,
        performance_is_hide: form.performanceIsHide,
        performance_objective: JSON.stringify({
          superior: form.performanceObjective.superior,
          excellent: form.performanceObjective.excellent,
          target: form.performanceObjective.target
        }),
        mention_user_ids: getFormattedUserIds(form.content, form.mentionUserIds)
      }

      const addUserIds = getAddUserIds(originalMentionUserIds, data.mention_user_ids[0][2])
      const rmUserIds = getRmUserIds(originalMentionUserIds, data.mention_user_ids[0][2])

      return updateObjective(props.objectiveId, data).then(() => {
        addAuth(addUserIds)
        removeAuth(rmUserIds)
        loading.value = false
        message.success(t('okr.editObjectiveDialog.successMessage'))
        emit('objective-saved')
      }).catch(e => {
        loading.value = false
      })
    }

    function addAuth (addUserIds) {
      if (addUserIds.length === 0) {
        return
      }
      editObjectiveAuth(form.id, addUserIds).then(res => {
        console.log(res)
      })
    }

    function removeAuth (rmUserIds) {
      if (rmUserIds.length === 0) {
        return
      }
      removeObjectiveAuth(form.id, rmUserIds).then(res => {
        console.log(res)
      })
    }

    return {
      formRef,
      form,
      save,
      loading
    }
  }
}
</script>

<style lang="less" scoped>
:deep(.ant-card:first-child) {
  margin-top: 24px;
}

:deep(.ant-card) {
  box-shadow: none !important;
}

:deep(.ant-card-body) {
  padding-top: 0;
  padding-bottom: 0;
}

.tip {
  color: red;
}

.title {
  margin-bottom: 18px;
  font-weight: 700;
}

:deep(.ant-form-item-label) {
  label {
    color: rgba(0, 0, 0, 0.56);
  }
}

.input {
  width: 68px;
}

.after {
  padding-left: 6px;
}
</style>
